import clsx from "clsx";
import { useEffect, useState } from "react";
import { CC_FIELD, CC_PANEL, CC_SECTION_LABEL } from "@/components/case-checking/caseCheckingChrome";
import { Button } from "@/components/ui/button/Button";

export type CompletenessVerdict = "agree" | "disagree";

export type CompletenessReviewState = {
  status: CompletenessVerdict;
  note: string;
};

export type AgentCompletenessCall = "complete" | "incomplete" | "pending";

export function ReviewCompletenessVerdict({
  value,
  agentCall,
  onSave,
  onDirtyChange,
}: {
  value: CompletenessReviewState | null;
  agentCall: AgentCompletenessCall;
  onSave: (next: CompletenessReviewState) => void;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const [draftStatus, setDraftStatus] = useState<CompletenessVerdict | null>(value?.status ?? null);
  const [draftNote, setDraftNote] = useState(value?.note ?? "");
  const [justSaved, setJustSaved] = useState(false);

  useEffect(() => {
    setDraftStatus(value?.status ?? null);
    setDraftNote(value?.note ?? "");
  }, [value?.status, value?.note]);

  const dirty =
    draftStatus !== (value?.status ?? null) || draftNote !== (value?.note ?? "");

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  useEffect(() => {
    if (!justSaved) return;
    const t = window.setTimeout(() => setJustSaved(false), 2500);
    return () => window.clearTimeout(t);
  }, [justSaved]);

  const canSave =
    draftStatus !== null &&
    (draftStatus === "agree" || draftNote.trim().length > 0);

  const agentLine =
    agentCall === "pending"
      ? "Agent has not finished the completeness check yet."
      : agentCall === "complete"
        ? "Agent says this file is complete."
        : "Agent says this file is incomplete.";

  return (
    <div className={CC_PANEL} data-testid="completeness-review-verdict">
      <p className={clsx(CC_SECTION_LABEL, "!mb-2")}>Your decision</p>
      <p className="text-sm text-slate-600 dark:text-slate-300">{agentLine}</p>
      <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
        Do you agree with the agent? Add a short note if you disagree or want to explain.
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button
          type="button"
          color={draftStatus === "agree" ? "indigo" : "dark/zinc"}
          onClick={() => setDraftStatus("agree")}
          data-testid="completeness-verdict-agree"
        >
          Agree with agent
        </Button>
        <Button
          type="button"
          color={draftStatus === "disagree" ? "red" : "dark/zinc"}
          onClick={() => setDraftStatus("disagree")}
          data-testid="completeness-verdict-disagree"
        >
          Disagree with agent
        </Button>
      </div>
      <div className="mt-3 space-y-2">
        <label className="block text-xs text-slate-500" htmlFor="completeness-verdict-note">
          Note{" "}
          {draftStatus === "disagree" ? (
            <span className="text-rose-700 dark:text-rose-300">(required to save)</span>
          ) : (
            <span className="text-slate-400">(optional)</span>
          )}
        </label>
        <textarea
          id="completeness-verdict-note"
          className={clsx(CC_FIELD, "resize-none")}
          rows={3}
          placeholder="e.g. KFD is in the file under a different name"
          value={draftNote}
          onChange={(e) => setDraftNote(e.target.value)}
        />
        {draftStatus === "disagree" && !draftNote.trim() ? (
          <p className="text-xs text-amber-800/90 dark:text-amber-300/90">
            Add a note explaining your disagreement before saving.
          </p>
        ) : null}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Button
          type="button"
          color="indigo"
          disabled={!canSave || !dirty}
          onClick={() => {
            if (!draftStatus || !canSave) return;
            onSave({ status: draftStatus, note: draftNote.trim() });
            setJustSaved(true);
          }}
          data-testid="completeness-verdict-save"
        >
          Save decision
        </Button>
        {value && !dirty ? (
          <span className="text-xs text-emerald-800 dark:text-emerald-300/90">Decision saved — you can continue.</span>
        ) : justSaved ? (
          <span className="text-xs text-emerald-800 dark:text-emerald-300/90">Saved.</span>
        ) : dirty && value ? (
          <span className="text-xs text-amber-800/90 dark:text-amber-300/90">Save again after changes.</span>
        ) : null}
      </div>
      {agentCall === "incomplete" ? (
        <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
          Missing files? Use <strong className="font-medium text-slate-600 dark:text-slate-300">Previous</strong> to
          open the Documents step and upload or pull from CRM.
        </p>
      ) : null}
    </div>
  );
}

export function isChecklistItemMissing(item: {
  checked: boolean;
  blocking?: boolean;
  present: boolean;
  notApplicable?: { reason: string };
}): boolean {
  return Boolean(item.checked && item.blocking && !item.present && !item.notApplicable);
}
